import moment from "moment";
import React, { useCallback, useEffect, useState } from "react";
import { useDispatch } from "react-redux";
import { useHistory, useParams } from "react-router-dom";

import BillPaymentList from "./components/BillPayment/BillPaymentList";
import BillTable from "./components/BillTable/BillTable";
import Header from "./components/Header/Header";

import { Spinner } from "../../../../../shared/ui/atoms/Spinner/Spinner";
import BackNavigationBar from "../../../../../shared/ui/layout/BackNavigationBar/BackNavigationBar";
import Paper from "../../../../../shared/ui/layout/Paper/Paper";
import {
  attachFilesToBill,
  changeBillDate,
  deleteFileFromBill,
  getBillDetailed,
  saveBillNumber,
} from "../model/thunks";
import { useTypedSelector } from "app/store/typedUseSelector";
import EmptyPlaceholder from "shared/ui/layout/EmptyPlaceholder/EmptyPlaceholder";

import { NOT_FOUND_PLACEHOLDER } from "constants/placeholders";

import useDebounce from "../../../../../utils/hooks/useDebouncedValue";

import notFoundIcon from "images/icons/not-found-icon-black.svg";

import styles from "./Bill.module.scss";

interface IProps {
  permissions: Record<string, any>;
  isSimplified?: boolean;
}

function Bill({ permissions, isSimplified }: IProps) {
  const { idBill } = useParams<Record<string, any>>();
  const history = useHistory();
  const from = new URLSearchParams(history.location.search).get("from");

  const dispatch = useDispatch();
  const openedBill = useTypedSelector((state) => state.billsV2.detailedBills)[idBill];
  const openedBillLoading = useTypedSelector((state) => state.billsV2.isLoading)[idBill];
  const isOpenedBillNotFound = !openedBillLoading && !openedBill;
  const invalidateKey = useTypedSelector((state) => state.billsV2.invalidateKey);

  const handleAddFiles = useCallback(
    (addedFiles: File[]) => {
      dispatch(attachFilesToBill(idBill, Array.from(addedFiles)));
    },
    [idBill]
  );

  const handleDeleteFile = useCallback(
    (deletedFileId: number) => {
      if (!deletedFileId) return;
      dispatch(deleteFileFromBill(idBill, deletedFileId));
    },
    [idBill]
  );

  const [numberBill, setNumberBill] = useState<string>();
  const debouncedNumberBill = useDebounce(numberBill, 500);

  const onChangeDate = useCallback(
    (changedDate: any) => {
      dispatch(changeBillDate(idBill, moment(changedDate).format("YYYY-MM-DD")));
    },
    [idBill]
  );

  useEffect(() => {
    setNumberBill(openedBill?.number);
  }, [openedBill]);

  useEffect(() => {
    if (!debouncedNumberBill || !openedBill || openedBill.number === debouncedNumberBill) return;
    dispatch(saveBillNumber(idBill, debouncedNumberBill));
  }, [debouncedNumberBill, openedBill]);

  useEffect(() => {
    if (!idBill) return;
    dispatch(getBillDetailed(idBill));
  }, [idBill, invalidateKey]);

  if (openedBillLoading) return <Spinner />;

  if (isOpenedBillNotFound) return <EmptyPlaceholder img={notFoundIcon} text={NOT_FOUND_PLACEHOLDER} />;

  return (
    <div className={styles.container}>
      {!isSimplified && (
        <BackNavigationBar title="Документы" backLink={from!} rightSideText={openedBill?.building.name} />
      )}
      <Paper>
        <div className={styles.content}>
          <Header
            bill={openedBill}
            number={numberBill}
            onChangeNumber={setNumberBill}
            onChangeDate={onChangeDate}
            addFiles={handleAddFiles}
            deleteFile={handleDeleteFile}
            permissions={permissions}
          />
          <BillTable
            data={openedBill?.items}
            amount={openedBill?.calculation?.amount}
            delivery={openedBill?.calculation?.amount_delivery}
            vat={openedBill?.calculation?.amount_vat}
            total={openedBill?.calculation?.amount_total}
            comment={openedBill?.comment}
          />
          <BillPaymentList
            parentId={idBill}
            payments={openedBill?.payments!}
            permissions={permissions}
            isSimplified={isSimplified}
          />
        </div>
      </Paper>
    </div>
  );
}

export default React.memo(Bill);
